import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ClipboardList, Send } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { pick } from "../lib/pick";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.5 },
};

export const LiveOutline = ({ outline }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <section id="outline" className="max-w-7xl mx-auto px-5 sm:px-6 pt-20" data-testid="live-outline">
      <motion.div {...fade}>
        <span className="eyebrow">
          <span className="w-7 h-px bg-[#1D4ED8]" />
          {t("live.outlineEyebrow")}
        </span>
        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5">{t("live.outlineTitle")}</h2>
        <p className="text-[#475569] mt-4 max-w-2xl">{t("live.outlineSubtitle")}</p>
      </motion.div>

      <div className="mt-12 space-y-4">
        {outline.map((m, i) => (
          <motion.div
            key={m.month}
            {...fade}
            transition={{ duration: 0.45, delay: Math.min(i, 6) * 0.05 }}
            className="card-dark p-6 sm:p-8"
            data-testid={`live-outline-month-${m.month}`}
          >
            <div className="flex flex-wrap items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-[#1D4ED8] text-white font-heading font-black text-lg flex items-center justify-center">
                {String(m.month).padStart(2, "0")}
              </span>
              <h3 className="font-heading font-black text-xl sm:text-2xl flex-1 min-w-[220px]">{pick(m, "title", lang)}</h3>
              <span className="badge-outline">{t("sales.month")} {m.month}</span>
            </div>
            <div className="gold-rule mt-6" />
            <ul className="mt-5 space-y-3">
              {(lang === "en" ? m.points_en || m.points : m.points).map((p, pi) => (
                <li key={pi} className="flex items-start gap-3 text-sm text-[#334155]">
                  <CheckCircle2 className="w-4 h-4 text-[#1D4ED8] shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            {isAdmin && (
            <div className="mt-6 rounded-2xl border-2 border-[#1D4ED8] bg-[#EEF4FF] p-5" data-testid={`live-assignment-${m.month}`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8] flex items-center gap-2">
                <ClipboardList className="w-3.5 h-3.5" /> {t("live.assignment")}
              </p>
              <p className="font-bold text-[#0B1B3A] mt-2 text-sm">{pick(m, "assignment", lang)}</p>
            </div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const InterestForm = ({ programTitle }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/leads", { ...form, program: programTitle });
      setState("ok");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setState("error");
    }
  };

  const field = "w-full rounded-2xl border border-[#E2E8F0] px-5 py-3.5 text-sm text-[#0B1B3A] placeholder:text-[#94A3B8] focus:border-[#1D4ED8] focus:outline-none transition-colors duration-200";

  return (
    <section id="interest" className="max-w-7xl mx-auto px-5 sm:px-6 pt-20" data-testid="live-interest-section">
      <motion.div {...fade} className="card-dark p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <span className="eyebrow">
            <span className="w-7 h-px bg-[#1D4ED8]" />
            {t("live.badge")}
          </span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mt-5 text-[#0B1B3A]">{t("live.interestTitle")}</h2>
          <p className="text-[#475569] mt-4">{t("live.interestText")}</p>
          <p className="text-sm text-[#64748B] mt-6">{t("live.seatsV")} · {t("live.durationV")}</p>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3" data-testid="live-interest-form">
          <input
            required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t("live.interestName")} className={field} data-testid="interest-name"
          />
          <input
            required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder={t("live.interestEmail")} className={field} data-testid="interest-email"
          />
          <input
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder={t("live.interestPhone")} className={field} data-testid="interest-phone"
          />
          <textarea
            rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder={t("live.interestMessage")} className={field} data-testid="interest-message"
          />
          <button type="submit" disabled={state === "sending"} className="btn-blue mt-2 disabled:opacity-60" data-testid="interest-submit">
            {state === "sending" ? t("common.loading") : t("live.interestSend")} <Send className="w-4 h-4" />
          </button>
          {state === "ok" && <p className="text-sm font-bold text-[#0F9D58]" data-testid="interest-success">{t("live.interestOk")}</p>}
          {state === "error" && <p className="text-sm font-bold text-[#DC2626]" data-testid="interest-error">{t("live.interestErr")}</p>}
        </form>
      </motion.div>
    </section>
  );
};
